import { defaultBackendUrl, Severity } from './constants';

type BeholderConfig = {
  /**
   * URL to Beholder backend. Should match the `mountUrl` config setting.
   */
  backendUrl?: string;
  /**
   * Options that are passed to the `fetch` call. Feel free to add
   * authorization cookies here, and stuff of that matter.
   */
  fetchOptions?: RequestInit | (() => RequestInit);
};

type Context = {
  tags?: Record<string, unknown>;
  level?: Severity;
  extra?: unknown;
  user?: unknown;
  fingerprint?: unknown;
  [key: string]: unknown;
};

const knownErrorProperties = ['name', 'message', 'stack', 'code'];

// prettier-ignore
export const Beholder = new class Beholder {
  initialized = false;
  config: BeholderConfig = {};
  messageQueue: any[] = [];

  initialize(config: BeholderConfig) {
    this.config = config;
    this.initialized = true;

    // Empty the message queue
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  sendMessage(message: Record<string, any>) {
    if (!this.initialized) {
      this.messageQueue.push(message);
      return;
    }

    const backendUrl = this.config.backendUrl || defaultBackendUrl;
    const fetchOptions = typeof this.config.fetchOptions === 'function'
      ? this.config.fetchOptions()
      : this.config.fetchOptions;
    // prettier-ignore
    fetch(backendUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...fetchOptions,
      method: 'POST',
      headers: {
        ...fetchOptions?.headers,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    }).catch((err) => {
      if (process.env['NODE_ENV'] !== 'production') {
        // eslint-disable-next-line
        console.warn('Failed to send a beholder message:', err, { message });
      }
    });
  }

  // TODO: API stub, to be implemented.
  setUser(...args: any[]) {
    if (process.env['NODE_ENV'] !== 'production') {
      // eslint-disable-next-line
      console.info('FIXME: Beholder.setUser() stub was called.', args);
    }
  }

  captureException(error: any, context?: Context) {
    let payload = error;

    if (error instanceof Error) {
      payload = { __error__: true };
      const keys = [...knownErrorProperties, ...Object.getOwnPropertyNames(error)];

      for (const key of keys) {
        if (key in payload) continue;

        payload[key] = (error as any)[key];
      }
    }

    this.sendMessage({ type: 'captureException', payload, context });
  }

  captureMessage(message: string, context?: Context) {
    this.sendMessage({ type: 'captureMessage', payload: {message}, context });
  }

  captureEvent(event: any, context?: Context) {
    let payload = event;

    if (event instanceof Event) {
      payload = { __event__: true };

      for (const key in event) {
        const value = (event as any)[key];

        // Skip functions
        if (typeof value === 'function') continue;

        // Skip DOM nodes
        if (value instanceof window.Node) continue;

        payload[key] = value;
      }
    }

    this.sendMessage({ type: 'captureEvent', payload, context });
  }
};

// Alias for backwards compatibility with Sentry
export const Sentry = Beholder;

// Verify that we are running in the browser
// Would not be necessary if we had multiple entry points in this package
if (typeof window !== 'undefined' && window.addEventListener) {
  // Global event handling
  const parentOnErrorHandler = window.onerror;

  window.onerror = (msg, url, line, col, error) => {
    // Proper stacktrace
    let stack = error && error.stack;

    // Makeshift stacktrace
    if (!stack) {
      stack = `${msg}\n   at ${url}:${line}`;

      if (col) {
        stack += `:${col}`;
      }
    }

    // If there is no error object, use the reconstructed stack trace
    const errorObj = error || { __error__: true, stack };

    Beholder.captureException(errorObj, {
      severity: Severity.Error,
      tags: {
        globalError: true,
      },
    });

    if (parentOnErrorHandler) {
      parentOnErrorHandler(msg, url, line, col, error);
    }
  };

  window.addEventListener('unhandledrejection', (e) => {
    Beholder.captureException(e.reason, {
      severity: Severity.Error,
      tags: {
        unhandledRejection: true,
      },
    });
  });
}
